import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import axios from 'axios';
import { 
  MapPin, 
  Calendar, 
  Users, 
  Loader2,
  Building2
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

const CityPage = () => {
  const { citySlug } = useParams();
  const [cityData, setCityData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCityData();
  }, [citySlug]);

  const fetchCityData = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/cities/${citySlug}`);
      setCityData(response.data);
    } catch (error) {
      console.error('Failed to fetch city data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-bone pt-20 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-gold" />
      </div>
    );
  }

  if (!cityData?.city) {
    return (
      <div className="min-h-screen bg-bone pt-20">
        <div className="container-padding py-16 text-center">
          <MapPin className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
          <h1 className="font-display text-2xl text-deep-green mb-2">City Not Found</h1>
          <p className="text-muted-foreground">This city hub doesn't exist yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-bone pt-20 md:pt-24">
      {/* Header */}
      <div className="bg-deep-green py-12 md:py-16">
        <div className="container-padding">
          <Badge className="bg-gold/20 text-gold mb-4">City Hub</Badge>
          <h1 className="font-display font-bold text-3xl md:text-4xl text-white mb-2">
            {cityData.city.city}, {cityData.city.state}
          </h1>
          <p className="text-cream/80 font-body">
            Connect with golfers, courses, and events in {cityData.city.city}
          </p>
        </div>
      </div>

      <div className="container-padding py-8">
        <Tabs defaultValue="courses">
          <TabsList className="bg-white shadow-card mb-8">
            <TabsTrigger value="courses" className="data-[state=active]:bg-fairway data-[state=active]:text-white">
              <MapPin className="w-4 h-4 mr-2" />
              Courses ({cityData.courses?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="events" className="data-[state=active]:bg-fairway data-[state=active]:text-white">
              <Calendar className="w-4 h-4 mr-2" />
              Events ({cityData.events?.length || 0})
            </TabsTrigger>
            <TabsTrigger value="golfers" className="data-[state=active]:bg-fairway data-[state=active]:text-white">
              <Users className="w-4 h-4 mr-2" />
              Golfers ({cityData.golfers?.length || 0})
            </TabsTrigger>
          </TabsList>

          {/* Courses */}
          <TabsContent value="courses">
            {cityData.courses?.length === 0 ? (
              <div className="text-center py-16">
                <MapPin className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No courses in this area yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cityData.courses?.map((course) => (
                  <Link key={course.course_id} to={`/courses/${course.course_id}`}>
                    <Card className="card-premium hover-lift h-full">
                      {course.images?.[0] && (
                        <div className="aspect-video overflow-hidden">
                          <img src={course.images[0]} alt={course.name} className="w-full h-full object-cover" />
                        </div>
                      )}
                      <CardContent className="p-5">
                        <h3 className="font-display font-semibold text-lg text-deep-green mb-2">
                          {course.name}
                        </h3>
                        {course.minority_owned && (
                          <Badge className="bg-gold text-deep-green">Minority Owned</Badge>
                        )}
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Events */}
          <TabsContent value="events">
            {cityData.events?.length === 0 ? (
              <div className="text-center py-16">
                <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No upcoming events in this area.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
                {cityData.events?.map((event) => (
                  <Link key={event.event_id} to={`/events/${event.event_id}`}>
                    <Card className="card-premium hover-lift h-full">
                      <CardContent className="p-5">
                        <div className="flex items-center gap-2 text-gold text-sm mb-2">
                          <Calendar className="w-4 h-4" />
                          {format(new Date(event.start_at), 'MMM d, yyyy')}
                        </div>
                        <h3 className="font-display font-semibold text-lg text-deep-green mb-2">
                          {event.title}
                        </h3>
                        <Badge variant="outline">{event.type}</Badge>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>

          {/* Golfers */}
          <TabsContent value="golfers">
            {cityData.golfers?.length === 0 ? (
              <div className="text-center py-16">
                <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                <p className="text-muted-foreground">No golfers in this area yet.</p>
              </div>
            ) : (
              <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-4">
                {cityData.golfers?.map((golfer) => (
                  <Link key={golfer.uid} to={`/profile/${golfer.uid}`}>
                    <Card className="card-premium hover-lift">
                      <CardContent className="p-4 text-center">
                        <div className="w-16 h-16 rounded-full bg-gold/20 flex items-center justify-center mx-auto mb-3">
                          <span className="text-gold font-bold text-xl">
                            {golfer.display_name?.charAt(0) || 'G'}
                          </span>
                        </div>
                        <p className="font-semibold text-deep-green">{golfer.display_name}</p>
                        <p className="text-muted-foreground text-sm capitalize">{golfer.skill_level}</p>
                      </CardContent>
                    </Card>
                  </Link>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default CityPage;
